import { HTMLAttributes, useEffect, useRef, useState } from 'react';

export type Props = {
  id?: string;
  className?: string;
  initialValue: string;
  onChange?: (t: string) => void;
  onCommit?: (t: string) => void;
  onBlur?: (t: string) => void;
  inputMode?: HTMLAttributes<HTMLElement>['inputMode'];
  maxLength?: number;
  autoFocus?: boolean;
};

export function ContentEditableText({
  id,
  className,
  initialValue: _initialValue,
  onChange,
  onCommit,
  onBlur,
  inputMode,
  maxLength,
  autoFocus,
}: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [initialValue] = useState(_initialValue);

  useEffect(() => {
    if (autoFocus) {
      requestAnimationFrame(() => {
        // Не перехватываем фокус, если уже сфокусированы (иначе лишний select-all).
        if (ref.current && document.activeElement !== ref.current) {
          ref.current.focus();
        }
      });
    }
  }, [autoFocus]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ac = new AbortController();

    // Режим редактирования

    try {
      el.contentEditable = 'plaintext-only';
    } catch {
      // sic! workaround для firefox, который не поддерживает 'plaintext-only'
      el.contentEditable = 'true';
    }

    // События

    // - выбрать всё при фокусе
    el.addEventListener(
      'focus',
      () => {
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        sel?.removeAllRanges();
        sel?.addRange(range);
      },
      { signal: ac.signal },
    );

    // - санитайзинг при наборе
    el.addEventListener(
      'keydown',
      (e) => {
        if (
          // whitelist
          e.ctrlKey ||
          e.metaKey ||
          e.key === 'Backspace' ||
          e.key === 'Delete' ||
          e.key === 'Tab' ||
          e.key.startsWith('Arrow')
        ) {
          return;
        }

        if (e.key === 'Enter') {
          if (onCommit) {
            onCommit(el.innerText);
          }

          e.preventDefault();
        }

        if (inputMode === 'numeric' && !e.key.match(/\d/)) {
          e.preventDefault();
        }

        const selectedLength = getSelectionLength();
        if (maxLength && el.innerText.length - selectedLength >= maxLength) {
          e.preventDefault();
        }
      },
      { signal: ac.signal },
    );

    // - санитайзинг буфера обмена
    el.addEventListener(
      'paste',
      (e: ClipboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const data = e.clipboardData?.getData('text/plain');
        if (!data) {
          return;
        }

        let sanitized = stripNewlines(data);

        if (inputMode === 'numeric') {
          sanitized = sanitized?.replaceAll(/[^\d]/g, '');
        }

        if (maxLength) {
          const selectedLength = getSelectionLength();
          const l = stripNewlines(el.innerText).length - selectedLength;
          sanitized = sanitized?.substring(0, Math.max(0, maxLength - l));
        }

        if (!sanitized) return;

        const selection = window.getSelection();
        if (selection?.rangeCount) {
          selection.deleteFromDocument();
          selection.getRangeAt(0).insertNode(document.createTextNode(sanitized));
          selection.collapseToEnd();
        }

        onChange?.(el.innerText);
      },
      { signal: ac.signal },
    );

    return () => ac.abort();
  }, [inputMode, maxLength, onChange, onCommit]);

  return (
    <span
      id={id}
      className={className}
      ref={ref}
      inputMode={inputMode}
      onInput={() => onChange?.(ref.current?.innerText ?? '')}
      onBlur={() => onBlur?.(ref.current?.innerText ?? '')}
    >
      {initialValue}
    </span>
  );
}

function getSelectionLength(): number {
  const sel = window.getSelection();
  return !sel || sel.isCollapsed ? 0 : stripNewlines(sel.toString()).length;
}

function stripNewlines(t: string): string {
  return t.replaceAll(/[\r\n]/g, '');
}
